import type { CachedInquiry, FieldAppointmentEntry, OutboxEntry } from '../../offline/data-db'
import { latestSubmissions, returnedToField } from '../../offline/outbox'

/**
 * Ce qui **attend l'agent** dans le journal de synchro — la pastille du rail et les compteurs de
 * l'écran Notifications lisent la même règle, écrite une fois.
 *
 * Quatre choses seulement, et aucune n'est un geste de l'agent :
 * - un contrôle que le bureau a **renvoyé** et que l'équipe n'a pas repris ;
 * - un dépôt que le serveur a **refusé** : le rejeu ne le reprend jamais (`offline/outbox.ts`),
 *   il attend une décision humaine ;
 * - un envoi **en échec** (`failed` : trop lourd, format refusé…) : pas rejoué non plus
 *   (`REPLAYABLE`), et son motif dit de prévenir le bureau ;
 * - un **rendez-vous pris sur place** que le serveur a refusé (`failed`, revue de #370) : la
 *   tablette l'a montré à l'agenda, et sans ce signal l'agent croirait l'usager planifié.
 *
 * ⚠️ **Jamais les envois en attente** : ils repartiront seuls, et les compter dirait « 3 à
 * traiter » à propos de trois gestes que l'agent vient de faire. C'est l'objection qui tenait le
 * rail sans pastille ; elle vaut pour un compteur de non-lus, pas pour un compteur de travail.
 *
 * ⚠️ **Pas de marqueur « vu »** : le compte retombe quand l'agent **reprend** le contrôle, pas
 * quand il a jeté un œil. Un drapeau de lecture ferait disparaître le signal d'un travail qui
 * reste à faire.
 *
 * Le **dernier** envoi de chaque contrôle fait foi : le journal garde aussi ceux qu'un plus
 * récent a remplacés, et un refus repris ne doit plus compter.
 */
export interface Attention {
  returned: OutboxEntry[]
  refused: OutboxEntry[]
  failed: OutboxEntry[]
  refusedAppointments: FieldAppointmentEntry[]
  count: number
}

export function attentionOf(
  entries: readonly OutboxEntry[],
  inquiries: readonly CachedInquiry[],
  appointments: readonly FieldAppointmentEntry[] = [],
): Attention {
  const servedAt = new Map(inquiries.map((inquiry) => [inquiry.id, inquiry.servedAt]))
  const latest = [...latestSubmissions(entries).values()]

  const returned = latest.filter(
    (entry) =>
      entry.state === 'sent' &&
      returnedToField({ servedAt: servedAt.get(entry.inquiryId), sentAt: entry.sentAt }),
  )
  const refused = latest.filter((entry) => entry.state === 'rejected')
  const failed = latest.filter((entry) => entry.state === 'failed')

  const refusedAppointments = appointments.filter((entry) => entry.state === 'failed')

  return {
    returned,
    refused,
    failed,
    refusedAppointments,
    count: returned.length + refused.length + failed.length + refusedAppointments.length,
  }
}
